import { redirect } from "@tanstack/react-router";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { ArrowRight, BriefcaseBusiness, MapPin, Search, X } from "lucide-react";
import { PublicNav } from "@/components/site/PublicNav";
import { ProfileSearch } from "@/components/social/ProfileSearch";
import { FeedFilters, type FeedFilterState } from "@/components/feed/FeedFilters";
import { MarketplaceFeed } from "@/components/feed/MarketplaceFeed";
import { useAuthState } from "@/hooks/use-auth-state";
import { getFeedCentre } from "@/lib/homes.functions";
import { listMyOffers } from "@/lib/partner.functions";
import { bidWinCost } from "@/lib/credit-pricing";
import { gbp, sc } from "@/lib/format";

/** Filters live in the URL so coming back from a job keeps your place. */
type FeedSearch = Partial<FeedFilterState> & { tab?: "feed" | "people"; q?: string };

function num(v: unknown): number | null {
  const n = typeof v === "string" ? Number(v) : typeof v === "number" ? v : NaN;
  return Number.isFinite(n) ? n : null;
}

export const Route = createFileRoute("/feed")({
  // Retired marketplace screen: hidden from visitors, data kept.
  beforeLoad: () => { throw redirect({ to: "/guides" }); },
  validateSearch: (search: Record<string, unknown>): FeedSearch => ({
    tab: search["tab"] === "people" ? "people" : "feed",
    q: typeof search["q"] === "string" ? search["q"].slice(0, 120) : "",
    trade: typeof search["trade"] === "string" ? (search["trade"] as string) : "all",
    radius: num(search["radius"]),
    minPrice: num(search["minPrice"]),
    maxPrice: num(search["maxPrice"]),
    days: num(search["days"]),
    urgent: search["urgent"] === true || search["urgent"] === "true",
    saved: search["saved"] === true || search["saved"] === "true",
    following: search["following"] === true || search["following"] === "true",
    minRating: num(search["minRating"]),
  }),
  head: () => ({
    meta: [
      { title: "Shields Feed — jobs and finished work near you" },
      {
        name: "description",
        content:
          "Shield Partners see jobs people nearby need doing. Customers see real finished work they can book something similar to.",
      },
      { property: "og:title", content: "Shields Feed — jobs and finished work near you" },
      {
        property: "og:description",
        content: "Local job requests and photos of finished work from Shields members near you.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: FeedPage,
});

function FeedPage() {
  const { signedIn } = useAuthState();
  const navigate = useNavigate({ from: "/feed" });
  const search = Route.useSearch();
  const [audience, setAudience] = useState<"partner" | "customer">("customer");
  const [queryDraft, setQueryDraft] = useState(search.q ?? "");

  useEffect(() => setQueryDraft(search.q ?? ""), [search.q]);

  const { data: centre } = useQuery({
    queryKey: ["feed-centre"],
    queryFn: () => getFeedCentre(),
    enabled: signedIn,
    staleTime: 300_000,
  });

  const setFilters = useCallback(
    (next: Partial<FeedFilterState>) => {
      void navigate({ search: (prev) => ({ ...prev, ...next }), replace: true });
    },
    [navigate],
  );

  const filters: FeedFilterState = {
    trade: search.trade ?? "all",
    radius: search.radius ?? null,
    minPrice: search.minPrice ?? null,
    maxPrice: search.maxPrice ?? null,
    days: search.days ?? null,
    urgent: search.urgent ?? false,
    saved: search.saved ?? false,
    following: search.following ?? false,
    minRating: search.minRating ?? null,
  };
  const tab = search.tab ?? "feed";

  const onAudience = useCallback((a: "partner" | "customer") => setAudience(a), []);

  return (
    <div className="min-h-screen pb-28">
      <PublicNav />
      <main className="product-page layout-feed section-pad mx-auto py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-extrabold text-foreground sm:text-3xl">Feed</h1>
            <p className="mt-1 text-sm font-medium text-foreground/70">
              {audience === "partner"
                ? "Jobs wanted near you — bid, claim or message the customer."
                : "Finished work from Shield Partners near you — book something similar."}
            </p>
          </div>
          {centre?.label && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground/70">
              <MapPin className="h-3.5 w-3.5 text-primary" aria-hidden />
              {centre.radiusMiles} miles around {centre.label}
            </span>
          )}
        </div>

        <form
          className="mt-5 flex items-center gap-2 rounded-2xl border border-border bg-card p-2 shadow-sm focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/10"
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({
              search: (prev) => {
                const next = { ...prev };
                const trimmed = queryDraft.trim();
                if (trimmed) next.q = trimmed;
                else delete next.q;
                return next;
              },
              replace: true,
            });
          }}
          role="search"
        >
          <Search className="ml-2 h-5 w-5 shrink-0 text-primary" aria-hidden />
          <label htmlFor="feed-search" className="sr-only">Search the Shields feed</label>
          <input
            id="feed-search"
            value={queryDraft}
            onChange={(event) => setQueryDraft(event.target.value)}
            maxLength={120}
            placeholder={audience === "partner" ? "Search jobs, trades or places" : "Search work, partners or services"}
            className="min-h-11 min-w-0 flex-1 bg-transparent px-1 text-base font-medium outline-none placeholder:text-muted-foreground"
          />
          {queryDraft && (
            <button
              type="button"
              onClick={() => {
                setQueryDraft("");
                void navigate({
                  search: (prev) => {
                    const next = { ...prev };
                    delete next.q;
                    return next;
                  },
                  replace: true,
                });
              }}
              className="grid h-10 w-10 place-items-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Clear feed search"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
          <button type="submit" className="min-h-11 rounded-xl bg-primary px-4 text-sm font-extrabold text-primary-foreground">
            Search
          </button>
        </form>

        <div className="mt-4 flex gap-1 rounded-full border border-border bg-card p-1">
          {(["feed", "people"] as const).map((t) => (
            <button
              key={t}
              onClick={() => void navigate({ search: (prev) => ({ ...prev, tab: t }), replace: true })}
              className={`min-h-11 flex-1 rounded-full px-4 text-sm font-semibold transition ${
                tab === t
                  ? "bg-primary text-primary-foreground shadow-[var(--shadow-glow)]"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              {t === "feed" ? (audience === "partner" ? "Jobs wanted" : "Finished work") : "People"}
            </button>
          ))}
        </div>

        {!signedIn ? (
          <div className="mt-8 rounded-2xl border border-border bg-card p-6 text-center">
            <p className="text-lg font-bold text-foreground">Sign in to see your feed</p>
            <p className="mx-auto mt-1 max-w-md text-sm font-medium text-foreground/70">
              Customers see finished work from Shield Partners nearby. Shield Partners see the jobs
              people near them need doing.
            </p>
            <Link
              to="/auth"
              className="mt-4 inline-flex min-h-11 items-center justify-center rounded-xl bg-primary px-5 text-sm font-bold text-primary-foreground shadow-[var(--shadow-glow)]"
            >
              Sign in or create an account
            </Link>
          </div>
        ) : tab === "people" ? (
          <div className="mt-6">
            <ProfileSearch />
          </div>
        ) : (
          <>
            <div className="mt-4">
              <MatchedPartnerJobs />
              <FeedFilters audience={audience} value={filters} onChange={setFilters} />

            </div>
            <div className="mt-5">
              <MarketplaceFeed filters={filters} query={search.q ?? ""} onAudience={onAudience} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}


function MatchedPartnerJobs() {
  const { data: offers = [] } = useQuery({ queryKey: ["partner-offers", "feed"], queryFn: () => listMyOffers(), staleTime: 30_000 });
  if (offers.length === 0) return null;
  return <section className="mb-5 overflow-hidden rounded-2xl border border-primary/25 bg-[linear-gradient(145deg,rgba(7,26,56,.98),rgba(10,35,72,.94))] text-white shadow-[0_18px_50px_-30px_rgba(31,93,180,.9)]" aria-labelledby="matched-jobs-title">
    <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3"><div><p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-primary">Live marketplace</p><h2 id="matched-jobs-title" className="mt-0.5 text-base font-extrabold">Jobs matched to you</h2></div><BriefcaseBusiness className="h-5 w-5 text-primary" aria-hidden /></div>
    <div className="divide-y divide-white/10">{offers.slice(0, 3).map((offer) => { const estimateMid = offer.estimate_low_pence == null ? null : Math.round((offer.estimate_low_pence + (offer.estimate_high_pence ?? offer.estimate_low_pence)) / 2); const estimatedWinCredits = estimateMid == null ? null : bidWinCost(estimateMid); return <Link key={offer.id} to="/partner/offers/$offerId" params={{ offerId: offer.id }} className="flex min-h-20 items-center gap-3 px-4 py-3 transition hover:bg-white/5"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-primary/30 bg-primary/10"><BriefcaseBusiness className="h-5 w-5 text-primary" aria-hidden /></span><span className="min-w-0 flex-1"><strong className="block truncate text-sm">{offer.service_name || "Local job"}</strong><span className="mt-1 block text-xs text-white/65">{offer.outward_postcode}{estimateMid == null ? " · quote requested" : " · estimate " + gbp(estimateMid)}</span><span className="mt-1 block text-[11px] font-semibold text-primary">Bid free{estimatedWinCredits == null ? " · winning cost shown with your quote" : " · about " + sc(estimatedWinCredits) + " if your bid wins"}</span></span><span className="flex shrink-0 items-center gap-1 text-xs font-bold text-primary">Open <ArrowRight className="h-3.5 w-3.5" aria-hidden /></span></Link>; })}</div>
    <p className="border-t border-white/10 px-4 py-2.5 text-[11px] text-white/55">Open a job to quote, negotiate, bid, use Get Now or decline. The confirmed credit cost is shown before commitment.</p>
  </section>;
}
